// src/utils/helpers/numberFormat.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React from 'react';
import dayjs from 'dayjs';
import i18n from 'i18next';
import { toBcp47, getStaticLocaleFromApp } from './localeUtils';
import { logErrorSilent } from 'utils/errorLogger';

// *******************************************************************************
// MAPAS
// *******************************************************************************
const DEFAULT_CURRENCY_BY_COUNTRY = {
  us: 'USD',
  br: 'BRL',
  pt: 'EUR',
  gb: 'GBP',
  uk: 'GBP',
  ca: 'CAD',
  au: 'AUD',
  nz: 'NZD',
  in: 'INR',
  de: 'EUR',
  fr: 'EUR',
  es: 'EUR',
  it: 'EUR',
  mx: 'MXN',
  ar: 'ARS',
  cl: 'CLP',
  jp: 'JPY',
  cn: 'CNY',
  tw: 'TWD',
};

// *******************************************************************************
// HELPERS
// *******************************************************************************
const parseToNumber = v => {
  if (v === null || v === undefined || v === '') return 0;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  const s = String(v).trim();

  // "1.234,56" → 1234.56
  if (/^-?\d{1,3}(\.\d{3})*,\d+$/.test(s)) {
    return Number(s.replace(/\./g, '').replace(',', '.'));
  }
  // "1234.56" → 1234.56
  if (/^-?\d+(\.\d+)?$/.test(s)) {
    return Number(s);
  }
  // fallback
  return Number(s.replace(',', '.'));
};

const pickLocale = localeOpt => {
  const lang = (localeOpt || i18n?.language || 'en-us').toString().toLowerCase();
  return toBcp47(lang); // 'pt-BR', 'en-US'
};

const pickCountry = countryOpt => {
  if (countryOpt) return countryOpt.toLowerCase();

  try {
    const fromLS =
      typeof window !== 'undefined' && window.localStorage
        ? window.localStorage.getItem('countryCode')
        : null;
    if (fromLS) return fromLS.toLowerCase();
  } catch (error) {
    logErrorSilent(error, {
      context: 'numberFormat.pickCountry (localStorage)',
      extraData: { countryOpt },
    });
  }

  const staticLocale = getStaticLocaleFromApp(); // 'pt-br' | 'en-us'...
  const cc = getStaticLocaleFromApp(staticLocale, true); // 'BR'    | 'US' ...
  return cc || 'US';
};

const pickCurrency = (countryOpt, currencyOpt) => {
  if (currencyOpt) return currencyOpt.toUpperCase();
  const cc = pickCountry(countryOpt);
  return DEFAULT_CURRENCY_BY_COUNTRY[cc] || 'USD';
};

// arredondamento no nível de casas decimais
const applyRounding = (num, digits, direction) => {
  if (typeof digits !== 'number' || digits < 0) return num;
  const factor = 10 ** digits;

  // corta sem arredondar (truncate) → em direção a zero
  if (direction === 'none') {
    return Math.trunc(num * factor) / factor;
  }

  if (direction === 'up') {
    return Math.ceil(num * factor) / factor; // para +∞
  }

  if (direction === 'down') {
    return Math.floor(num * factor) / factor; // para -∞
  }

  // matemático padrão (0..4 ↓, 5..9 ↑)
  return Math.round((num + Number.EPSILON) * factor) / factor;
};

// *******************************************************************************
// CORE
// *******************************************************************************
/*
  options = {
    type: 'number' | 'currency' | 'percentage',
    as: 'string' | 'number',            // default 'string'
    locale,                             // 'pt-br' | 'en-us'...
    countryCode,                        // 'BR' | 'US'...
    currency,                           // 'BRL' | 'USD'...
    showSymbol,                         // default: number=false; currency/percentage=true
    symbol,                             // força símbolo ('R$', '$', 'USD', '%', etc)
    percentScale: 'unit' | 'hundred',   // 'unit' (0..1) | 'hundred' (0..100). default 'unit'

    // casas e arredondamento:
    decimals,                           // força min=max=decimals (formatação com zeros)
    minimumFractionDigits,
    maximumFractionDigits,
    round,                              // casas para arredondar/cortar o VALOR antes de formatar
    roundDirection,                     // 'up' | 'down' | 'none' | undefined (matemático)

    useGrouping = true,
    signDisplay = 'auto',               // 'auto' | 'always' | 'never' | 'exceptZero'
  }
*/
export const formatValue = (value, options = {}) => {
  const {
    type = 'number',
    as = 'string',
    locale,
    countryCode,
    currency,
    showSymbol,
    symbol,
    percentScale = 'unit',
    decimals,
    minimumFractionDigits,
    maximumFractionDigits,
    round,
    roundDirection,
    useGrouping = true,
    signDisplay = 'auto',
  } = options;

  const loc = pickLocale(locale); // 'pt-BR'
  const cc = pickCountry(countryCode); // 'BR'
  const cur = pickCurrency(cc, currency); // 'BRL'
  let num = parseToNumber(value);

  const defaults = {
    number: { min: 0, max: 2 },
    currency: { min: 2, max: 2 },
    percentage: { min: 0, max: 2 },
  };

  const minFD =
    typeof decimals === 'number' ? decimals : (minimumFractionDigits ?? defaults[type].min);
  const maxFD =
    typeof decimals === 'number' ? decimals : (maximumFractionDigits ?? defaults[type].max);

  // prepara factories
  const mkDecimal = () =>
    new Intl.NumberFormat(loc, {
      style: 'decimal',
      minimumFractionDigits: minFD,
      maximumFractionDigits: maxFD,
      useGrouping,
      signDisplay,
    });

  const mkCurrency = () =>
    new Intl.NumberFormat(loc, {
      style: 'currency',
      currency: cur,
      currencyDisplay: 'symbol',
      minimumFractionDigits: minFD,
      maximumFractionDigits: maxFD,
      useGrouping,
      signDisplay,
    });

  const mkPercent = () =>
    new Intl.NumberFormat(loc, {
      style: 'percent',
      minimumFractionDigits: minFD,
      maximumFractionDigits: maxFD,
      useGrouping,
      signDisplay,
    });

  const wantSymbol =
    typeof showSymbol === 'boolean' ? showSymbol : type === 'currency' || type === 'percentage';

  // NUMBER / CURRENCY: arredonda o valor direto
  if (type === 'number' || type === 'currency') {
    const rounded = typeof round === 'number' ? applyRounding(num, round, roundDirection) : num;

    if (type === 'number') {
      const out = mkDecimal().format(rounded);
      return as === 'number' ? Number(rounded.toFixed(maxFD)) : out;
    }

    // currency
    if (wantSymbol && !symbol) {
      const out = mkCurrency().format(rounded);
      return as === 'number' ? Number(rounded.toFixed(maxFD)) : out;
    }
    const decimal = mkDecimal().format(rounded);
    if (!wantSymbol) {
      return as === 'number' ? Number(rounded.toFixed(maxFD)) : decimal;
    }
    const out = `${symbol} ${decimal}`;
    return as === 'number' ? Number(rounded.toFixed(maxFD)) : out;
  }

  // PERCENTAGE: arredonda o VALOR exibido (em %), depois converte pra fração
  // ex.: value=0.12345 (unit) → displayed=12.345 → round(2) => 12.35 → fraction=0.1235
  if (type === 'percentage') {
    const displayed = percentScale === 'hundred' ? num : num * 100;
    const displayedRounded =
      typeof round === 'number' ? applyRounding(displayed, round, roundDirection) : displayed;
    const fraction = displayedRounded / 100;

    const outStr = mkPercent().format(fraction);

    if (!wantSymbol) {
      const onlyNumber = outStr.replace(/\s*%/u, '');
      return as === 'number' ? Number(displayedRounded.toFixed(maxFD)) : onlyNumber;
    }

    if (symbol) {
      const dec = mkDecimal().format(displayedRounded);
      const out = `${dec}${symbol === '%' ? '%' : ` ${symbol}`}`;
      return as === 'number' ? Number(displayedRounded.toFixed(maxFD)) : out;
    }

    return as === 'number' ? Number(displayedRounded.toFixed(maxFD)) : outStr;
  }

  // fallback
  const out = mkDecimal().format(num);
  return as === 'number' ? Number(num.toFixed(maxFD)) : out;
};

// *******************************************************************************
// WRAPPERS
// *******************************************************************************
export const formatCurrency = (value, opts = {}) =>
  formatValue(value, { ...opts, type: 'currency' });

export const formatNumber = (value, opts = {}) => formatValue(value, { ...opts, type: 'number' });

export const formatPercent = (value, opts = {}) =>
  formatValue(value, { ...opts, type: 'percentage' });

// *******************************************************************************
// EXEMPLOS (DemoFormats) — cola em qualquer lugar para validar visualmente
// *******************************************************************************
const DemoFormats = () => {
  const now = dayjs();
  const raw = 10.2397;
  const big = 12345.6789;

  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {/* DATA (locale-aware) */}
      <div>{'data (L) →'} {now.format('L')}</div>
      <div>{'data (LL) →'} {now.format('LL')}</div>
      <div>{'data (LTS) →'} {now.format('LTS')}</div>
      <div>{'data (YYYY-MM-DD) →'} {now.format('YYYY-MM-DD')}</div>
      {/* VALOR BRUTO */}
      <div>{'valor raw →'} {raw}</div>
      {/* MOEDA - padrão do app.LOCALE */}
      <div>{'moeda →'} {formatCurrency(raw)}</div>
      <div>{'moeda (sem símbolo) →'} {formatCurrency(raw, { showSymbol: false })}</div>
      <div>{'moeda (decimals: 6) →'} {formatCurrency(raw, { decimals: 6 })}</div>
      <div>{'moeda (como número) →'} {String(formatCurrency(raw, { as: 'number' }))}</div>
      {/* MOEDA - arredondamento */}
      <div>{'moeda [round=2, math] →'} {formatCurrency(raw, { round: 2 })}</div> {/* 10,24 (BR) */}
      <div>{'moeda [round=2, up] →'} {formatCurrency(raw, { round: 2, roundDirection: 'up' })}</div>
      <div>
        {'moeda [round=2, down] →'} {formatCurrency(raw, { round: 2, roundDirection: 'down' })}
      </div>
      <div>
        {'moeda [round=2, none] →'} {formatCurrency(raw, { round: 2, roundDirection: 'none' })}
      </div>
      {/* MOEDA - forçando país / moeda */}
      <div>{'moeda [us] →'} {formatCurrency(raw, { countryCode: 'US' })}</div>
      <div>{'moeda [br] →'} {formatCurrency(raw, { countryCode: 'BR' })}</div>
      <div>{'moeda [currency=EUR] →'} {formatCurrency(raw, { currency: 'EUR' })}</div>
      <div>
        {'moeda [locale en-us + BRL] →'} {formatCurrency(raw, { locale: 'en-us', currency: 'BRL' })}
      </div>
      {/* NÚMERO */}
      <div>{'número →'} {formatNumber(big)}</div>
      <div>{'número (sem agrupamento) →'} {formatNumber(big, { useGrouping: false })}</div>
      <div>{'número (decimals: 6) →'} {formatNumber(raw, { decimals: 6 })}</div>
      {/* NÚMERO - arredondamento */}
      <div>{'número [round=3, math] →'} {formatNumber(big, { round: 3 })}</div>
      <div>{'número [round=3, up] →'} {formatNumber(big, { round: 3, roundDirection: 'up' })}</div>
      <div>{'número [round=3, down] →'} {formatNumber(big, { round: 3, roundDirection: 'down' })}</div>
      <div>{'número [round=3, none] →'} {formatNumber(big, { round: 3, roundDirection: 'none' })}</div>
      {/* PERCENTUAL (unit → valor 0..1) */}
      <div>{'percent [unit] →'} {formatPercent(0.123456)}</div>
      <div>{'percent [unit, decimals: 4] →'} {formatPercent(0.123456, { decimals: 4 })}</div>
      {/* PERCENTUAL - arredondamento (unit) → arredonda o valor exibido (em %) */}
      <div>{'percent [unit, round=2, math] →'} {formatPercent(0.123456, { round: 2 })}</div>
      {/* 12,35% */}
      <div>
        {'percent [unit, round=2, up] →'} {formatPercent(0.123456, { round: 2, roundDirection: 'up' })}
      </div>
      <div>
        {'percent [unit, round=2, down] →'}{' '}
        {formatPercent(0.123456, { round: 2, roundDirection: 'down' })}
      </div>
      <div>
        {'percent [unit, round=2, none] →'}{' '}
        {formatPercent(0.123456, { round: 2, roundDirection: 'none' })}
      </div>
      {/* PERCENTUAL (hundred → valor 0..100) */}
      <div>{'percent [hundred] →'} {formatPercent(12.3456, { percentScale: 'hundred' })}</div>
      <div>
        {'percent [hundred, round=1] →'}{' '}
        {formatPercent(12.3456, { percentScale: 'hundred', round: 1 })}
      </div>
      <div>
        {'percent [hundred, round=1, up] →'}{' '}
        {formatPercent(12.3456, { percentScale: 'hundred', round: 1, roundDirection: 'up' })}
      </div>
      <div>
        {'percent [hundred, round=1, down] →'}{' '}
        {formatPercent(12.3456, { percentScale: 'hundred', round: 1, roundDirection: 'down' })}
      </div>
      <div>
        {'percent [hundred, round=1, none] →'}{' '}
        {formatPercent(12.3456, { percentScale: 'hundred', round: 1, roundDirection: 'none' })}
      </div>
      {/* SINAL */}
      <div>{'currency (+ sempre) →'} {formatCurrency(10, { signDisplay: 'always' })}</div>
      <div>{'currency (- sempre) →'} {formatCurrency(-10, { signDisplay: 'always' })}</div>
      <div>
        {'number (+ sempre, decimals: 2) →'} {formatNumber(10, { signDisplay: 'always', decimals: 2 })}
      </div>
      {/* GENÉRICO */}
      <div>
        {'formatValue currency BR, round=2, decimals=6 →'}{' '}
        {formatValue(raw, { type: 'currency', countryCode: 'BR', round: 2, decimals: 6 })}
      </div>
      <div>
        {'formatValue number en-us, round=3, as=number →'}{' '}
        {String(formatValue(big, { type: 'number', locale: 'en-us', round: 3, as: 'number' }))}
      </div>
      <div>
        {'formatValue percent unit, round=1, sem símbolo →'}{' '}
        {formatValue(0.129, { type: 'percentage', round: 1, showSymbol: false })}
      </div>
    </div>
  );
};

export default DemoFormats;
